import { Linkedin, Instagram, Mail, Globe } from 'lucide-react';

// Inline SVG avatar so we don't depend on external photo hosting.
// gender === 'female' renders longer hair + bun, 'male' renders short hair.
const Avatar = ({ name, gradient, gender }) => (
  <div
    role="img"
    aria-label={`${name} avatar`}
    className={`relative w-40 h-40 mx-auto rounded-full bg-gradient-to-br ${gradient} flex items-end justify-center overflow-hidden border-4 border-white shadow-lg`}
  >
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden="true">
      {/* Female: hair behind head + bun */}
      {gender === 'female' && (
        <>
          <circle cx="60" cy="41" r="29" fill="rgba(70,40,20,0.85)" />
          <circle cx="80" cy="24" r="10" fill="rgba(70,40,20,0.85)" />
        </>
      )}
      {/* Head */}
      <circle cx="60" cy="47" r="25" fill="rgba(255,255,255,0.96)" />
      {/* Male: short hair cap on top of head */}
      {gender === 'male' && (
        <path
          d="M34 42 A26 26 0 0 1 86 42 L86 40 A26 26 0 0 0 34 40 Z"
          fill="rgba(40,35,30,0.85)"
        />
      )}
      {/* Shoulders */}
      <path d="M20 120 C20 86 100 86 100 120 Z" fill="rgba(255,255,255,0.96)" />
    </svg>
  </div>
);

const LeadershipTeam = () => {
  const teamMembers = [
    {
      id: 1,
      name: 'Purba Banerjee',
      role: 'Founder & CEO',
      bio: 'Passionate about preserving Bengali heritage, Purba left her corporate career to build তন্তিকা. She holds a Masters in Cultural Anthropology from Jadavpur University.',
      gender: 'female',
      social: {
        linkedin: '#',
        instagram: '#',
        email: 'purba@tantika.com',
        website: '#'
      },
      color: 'from-clay-500 to-clay-600',
      quote: '"Every artisan we work with is preserving a piece of our culture."'
    },
    {
      id: 2,
      name: 'Aditya Mukherjee',
      role: 'Co-founder & COO',
      bio: 'With 10+ years in e-commerce operations, Aditya ensures that every তন্তিকা product reaches customers with care and efficiency. He oversees logistics and artisan relations.',
      gender: 'male',
      social: {
        linkedin: '#',
        instagram: '#',
        email: 'aditya@tantika.com',
        website: '#'
      },
      color: 'from-saffron-500 to-saffron-600',
      quote: '"We build bridges between tradition and modernity."'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-cream-100">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-clay-50 text-clay-600 rounded-full mb-4">
            <span className="font-medium">Leadership Team</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Meet Our Leadership
          </h2>
          <p className="text-cocoa-600 max-w-2xl mx-auto text-lg">
            Passionate individuals dedicated to preserving Bengali craftsmanship 
            while building a sustainable future for artisans.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16 max-w-4xl mx-auto">
          {teamMembers.map((member) => (
            <div 
              key={member.id}
              className="group relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              {/* Gradient Top Border */}
              <div className={`h-2 bg-gradient-to-r ${member.color}`}></div>
              
              <div className="p-8">
                {/* Profile Avatar */}
                <div className="relative mb-8">
                  <div className={`absolute inset-0 bg-gradient-to-r ${member.color} rounded-full opacity-20 blur-xl`}></div>
                  <Avatar name={member.name} gradient={member.color} gender={member.gender} />
                  
                  {/* Role Badge */}
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-gradient-to-r ${member.color} text-white px-6 py-2 rounded-full font-semibold shadow-lg`}>
                    {member.role}
                  </div>
                </div>

                {/* Name and Bio */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-3">{member.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{member.bio}</p>
                </div>

                {/* Quote */}
                <div className="bg-gradient-to-r from-clay-50 to-cream-100 rounded-xl p-6 mb-6 border-l-4 border-clay-500">
                  <p className="text-cocoa-700 italic">"{member.quote}"</p>
                </div>

                {/* Social Links */}
                <div className="flex justify-center space-x-4">
                  <a 
                    href={member.social.linkedin}
                    className="w-10 h-10 bg-clay-100 text-clay-600 rounded-full flex items-center justify-center hover:bg-clay-200 transition-colors"
                    aria-label={`Connect with ${member.name} on LinkedIn`}
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a 
                    href={member.social.instagram}
                    className="w-10 h-10 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center hover:bg-pink-200 transition-colors"
                    aria-label={`Follow ${member.name} on Instagram`}
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a 
                    href={`mailto:${member.social.email}`}
                    className="w-10 h-10 bg-cream-200 text-cocoa-700 rounded-full flex items-center justify-center hover:bg-cream-300 transition-colors"
                    aria-label={`Email ${member.name}`}
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                  <a 
                    href={member.social.website}
                    className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                    aria-label={`Visit ${member.name}'s website`}
                  >
                    <Globe className="w-5 h-5" />
                  </a>
                </div>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Team Philosophy */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-clay-50 to-saffron-50 rounded-3xl p-8 md:p-12 border border-cream-200">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4">Our Collective Vision</h3>
              <p className="text-cocoa-700 text-lg">
                We believe that businesses can be a force for good. Our leadership team combines 
                passion for cultural preservation with modern business expertise to create a platform 
                that benefits artisans, customers, and the cultural ecosystem of Bengal.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-3xl font-bold text-clay-600 mb-2">100%</div>
                <div className="text-cocoa-700 font-medium">Bengali Heritage</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-3xl font-bold text-saffron-600 mb-2">Fair</div>
                <div className="text-cocoa-700 font-medium">Trade Practices</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-3xl font-bold text-rose-600 mb-2">Sustainable</div>
                <div className="text-cocoa-700 font-medium">Growth Focus</div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-cocoa-600 mb-8 max-w-2xl mx-auto">
              Interested in joining our team or collaborating with us? We're always looking for 
              passionate individuals who share our vision.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:careers@tantika.com"
                className="bg-gradient-to-r from-clay-500 to-saffron-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Explore Careers
              </a>
              <a 
                href="mailto:partnerships@tantika.com"
                className="border-2 border-clay-500 text-clay-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-clay-50 transition-colors"
              >
                Partnership Inquiries
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadershipTeam;
